import { useEffect, useState, useCallback } from 'react';
import {
  ClipboardList, Download, Loader2, Search,
  ChevronLeft, ChevronRight, Filter, User, Shield, X, MapPin, Radio, Clock, Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { AlarmSite } from '@asi/shared';
import {
  fetchAuditLog, fetchAllSites, fetchOperators, getCidDescription, getEventPriority,
  type AuditFilters, type AuditLogEntry,
} from '@/services/arc';

// ─── Constants ────────────────────────────────────────────────

const PAGE_SIZE = 100;

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'text-red-600 font-bold',
  ALARM:    'text-orange-500 font-semibold',
  WARNING:  'text-yellow-600',
  INFO:     'text-blue-500',
};

const SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: 'Crítico', ALARM: 'Alarma', WARNING: 'Advertencia', INFO: 'Información',
};

const CATEGORY_LABELS: Record<string, string> = {
  alarm: 'Alarma', restore: 'Restauración', open_close: 'Apertura/Cierre',
  trouble: 'Falla', test: 'Prueba', system: 'Sistema', bypass: 'Bypass',
};

// ─── Helpers ──────────────────────────────────────────────────

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function daysAgoStr(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function fmtDatetime(iso: string) {
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function fmtResponseTime(s: number | null) {
  if (s === null) return '-';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function exportCSV(entries: AuditLogEntry[]) {
  const headers = [
    'Fecha procesado', 'Operador', 'Cliente', 'Cuenta',
    'Fecha evento', 'Código CID', 'Descripción', 'Severidad',
    'Categoría', 'T.Respuesta', 'Resolución',
  ];
  const rows = entries.map((e) => [
    e.resolved_at ? fmtDatetime(e.resolved_at) : '-',
    e.res_profile?.full_name ?? e.ack_profile?.full_name ?? '-',
    e.alarm_sites?.name ?? 'Sin asignar',
    e.alarm_sites?.account_number ?? '',
    fmtDatetime(e.received_at),
    e.cid_code,
    getCidDescription(e.cid_code),
    SEVERITY_LABELS[e.severity] ?? e.severity,
    CATEGORY_LABELS[e.event_category] ?? e.event_category,
    fmtResponseTime(e.response_time_seconds),
    e.resolution ?? '',
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `auditoria-arc-${todayStr()}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ─── Stat Badge ───────────────────────────────────────────────

function StatBadge({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="text-center">
      <div className={cn('text-2xl font-bold', color)}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({
    dateFrom: daysAgoStr(7),
    dateTo: todayStr(),
    operatorId: '',
    siteId: '',
    severity: '',
  });
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sites, setSites] = useState<Pick<AlarmSite, 'id' | 'name'>[]>([]);
  const [operators, setOperators] = useState<{ id: string; full_name: string; email: string }[]>([]);
  const [search, setSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    fetchAllSites().then(setSites).catch(console.error);
    fetchOperators().then(setOperators).catch(console.error);
  }, []);

  const doFetch = useCallback(async (f: AuditFilters, p: number) => {
    setLoading(true);
    try {
      const { data, total: t } = await fetchAuditLog(f, p, PAGE_SIZE);
      setEntries(data);
      setTotal(t);
    } catch (e) {
      console.error('[Audit] fetch error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    doFetch(filters, page);
  }, [filters, page, doFetch]);

  // Client-side text search on loaded page
  const displayEntries = search.trim()
    ? entries.filter((e) => {
        const q = search.toLowerCase();
        const operator = (e.res_profile?.full_name ?? e.ack_profile?.full_name ?? '').toLowerCase();
        return (
          e.cid_code.toLowerCase().includes(q) ||
          (e.alarm_sites?.name?.toLowerCase().includes(q) ?? false) ||
          (e.alarm_sites?.account_number?.toLowerCase().includes(q) ?? false) ||
          getCidDescription(e.cid_code).toLowerCase().includes(q) ||
          (e.resolution?.toLowerCase().includes(q) ?? false) ||
          operator.includes(q)
        );
      })
    : entries;

  // Derived stats
  const avgResponse = (() => {
    const times = displayEntries
      .filter((e) => e.response_time_seconds !== null)
      .map((e) => e.response_time_seconds!);
    return times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
  })();

  const criticalCount = displayEntries.filter((e) => e.severity === 'CRITICAL' || e.severity === 'ALARM').length;

  // Unique operators who processed events in this view
  const operatorBreakdown = displayEntries.reduce<Record<string, number>>((acc, e) => {
    const name = e.res_profile?.full_name ?? e.ack_profile?.full_name ?? 'Desconocido';
    acc[name] = (acc[name] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-7 w-7" />
            Auditoría de eventos ARC
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Registro de todos los eventos procesados por operadores
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => exportCSV(entries)} disabled={entries.length === 0}>
          <Download className="h-4 w-4 mr-1.5" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Desde</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => { setFilters((f) => ({ ...f, dateFrom: e.target.value })); setPage(0); }}
                className="w-40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Hasta</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => { setFilters((f) => ({ ...f, dateTo: e.target.value })); setPage(0); }}
                className="w-40"
              />
            </div>

            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground font-medium">Operador</label>
              <Select
                value={filters.operatorId || '__all__'}
                onValueChange={(v) => { setFilters((f) => ({ ...f, operatorId: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los operadores</SelectItem>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.full_name || op.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground font-medium">Cliente / Sitio</label>
              <Select
                value={filters.siteId || '__all__'}
                onValueChange={(v) => { setFilters((f) => ({ ...f, siteId: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los sitios</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Severidad</label>
              <Select
                value={filters.severity || '__all__'}
                onValueChange={(v) => { setFilters((f) => ({ ...f, severity: v === '__all__' ? '' : v })); setPage(0); }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  <SelectItem value="CRITICAL">Crítico</SelectItem>
                  <SelectItem value="ALARM">Alarma</SelectItem>
                  <SelectItem value="WARNING">Advertencia</SelectItem>
                  <SelectItem value="INFO">Información</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground self-center" />}

            {/* Text search */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Código, cliente, operador..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-52 h-9 text-sm"
                />
              </div>
            </div>

            {/* Quick presets */}
            <div className="flex gap-1 ml-auto">
              {[
                { label: 'Hoy', from: todayStr(), to: todayStr() },
                { label: '7d', from: daysAgoStr(7), to: todayStr() },
                { label: '30d', from: daysAgoStr(30), to: todayStr() },
              ].map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="text-xs h-8 px-2"
                  onClick={() => {
                    setFilters((f) => ({ ...f, dateFrom: preset.from, dateTo: preset.to }));
                    setPage(0);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats row */}
      {!loading && total > 0 && (
        <div className="flex items-center gap-6 mb-4 px-1">
          <StatBadge label="Procesados" value={search ? displayEntries.length : total} color="text-foreground" />
          <div className="w-px h-8 bg-border" />
          <StatBadge label="Alarmas/Críticos" value={criticalCount} color="text-red-600" />
          {avgResponse !== null && (
            <>
              <div className="w-px h-8 bg-border" />
              <StatBadge label="T. resp. promedio" value={fmtResponseTime(avgResponse)} color="text-foreground" />
            </>
          )}
          {Object.keys(operatorBreakdown).length > 0 && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 flex-wrap">
                {Object.entries(operatorBreakdown)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 4)
                  .map(([name, count]) => (
                    <Badge key={name} variant="secondary" className="text-xs gap-1">
                      <User className="h-3 w-3" />
                      {name}: {count}
                    </Badge>
                  ))}
              </div>
            </>
          )}
          <div className="ml-auto text-sm text-muted-foreground">
            {search ? `${displayEntries.length} de ` : ''}Página {page + 1} de {Math.max(1, totalPages)} · {total} registros
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto rounded-lg border">
        {/* Header */}
        <div className="grid grid-cols-[90px_65px_160px_1fr_70px_90px_1fr_80px_80px] gap-2 px-3 py-2 bg-muted/50 border-b text-xs font-semibold text-muted-foreground sticky top-0">
          <div>Procesado</div>
          <div>Hora</div>
          <div>Operador</div>
          <div>Cliente</div>
          <div className="text-center">Código</div>
          <div>Zona</div>
          <div>Resolución</div>
          <div className="text-center">Severidad</div>
          <div className="text-center">T. Resp.</div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm">Cargando...</span>
          </div>
        )}

        {/* Empty */}
        {!loading && displayEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <Filter className="h-10 w-10 opacity-30" />
            <p className="text-sm">Sin eventos procesados para los filtros seleccionados</p>
          </div>
        )}

        {/* Rows */}
        {!loading && displayEntries.map((e, idx) => {
          const operatorName = e.res_profile?.full_name ?? e.ack_profile?.full_name ?? '—';
          const processedAt = e.resolved_at ?? e.acknowledged_at;

          return (
            <div
              key={e.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedEntry(e)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') setSelectedEntry(e); }}
              className={cn(
                'grid grid-cols-[90px_65px_160px_1fr_70px_90px_1fr_80px_80px] gap-2 px-3 py-2 border-b last:border-b-0 text-sm cursor-pointer',
                'hover:bg-muted/30 transition-colors',
                idx % 2 === 0 ? 'bg-background' : 'bg-muted/10',
              )}
            >
              {/* Fecha procesado */}
              <div className="font-mono text-xs text-muted-foreground flex items-center">
                {processedAt
                  ? new Date(processedAt).toLocaleDateString('es-GT', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      timeZone: 'America/Guatemala',
                    })
                  : '-'}
              </div>
              {/* Hora */}
              <div className="font-mono text-xs text-muted-foreground flex items-center">
                {processedAt
                  ? new Date(processedAt).toLocaleTimeString('es-GT', {
                      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
                      timeZone: 'America/Guatemala',
                    })
                  : '-'}
              </div>
              {/* Operador */}
              <div className="flex items-center gap-1.5 min-w-0">
                <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-xs font-medium">{operatorName}</span>
              </div>
              {/* Cliente */}
              <div className="flex items-center min-w-0">
                <div className="truncate">
                  <span className="font-semibold text-foreground text-xs">
                    {e.alarm_sites?.name ?? (
                      <span className="text-muted-foreground italic">Sin asignar</span>
                    )}
                  </span>
                  {e.alarm_sites?.account_number && (
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                      #{e.alarm_sites.account_number}
                    </span>
                  )}
                </div>
              </div>
              {/* Código CID */}
              <div className="flex items-center justify-center">
                <span className="font-mono text-xs font-medium">{e.cid_code}</span>
              </div>
              {/* Zona */}
              <div className="flex items-center text-xs text-muted-foreground">
                {e.zone_no != null ? (
                  <span title={e.zone_name ?? undefined} className="truncate">
                    Z{e.zone_no}{e.zone_name ? ` · ${e.zone_name}` : ''}
                  </span>
                ) : '-'}
              </div>
              {/* Resolución */}
              <div className="flex items-center min-w-0">
                <span className="truncate text-xs text-muted-foreground" title={e.resolution ?? '-'}>
                  {e.resolution ?? <span className="italic">—</span>}
                </span>
              </div>
              {/* Severidad */}
              <div className="flex items-center justify-center">
                <span className={cn('text-xs', SEVERITY_COLORS[e.severity])}>
                  {SEVERITY_LABELS[e.severity] ?? e.severity}
                </span>
              </div>
              {/* T. Respuesta */}
              <div className="flex items-center justify-center">
                <span className="font-mono text-xs text-muted-foreground">
                  {fmtResponseTime(e.response_time_seconds)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {selectedEntry && (
        <AuditEventDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground break-words">{value}</div>
      </div>
    </div>
  );
}

function AuditEventDetailModal({ entry: e, onClose }: { entry: AuditLogEntry; onClose: () => void }) {
  const operatorName = e.res_profile?.full_name ?? e.ack_profile?.full_name ?? '—';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <span className={cn(SEVERITY_COLORS[e.severity])}>{SEVERITY_LABELS[e.severity] ?? e.severity}</span>
              <span className="text-muted-foreground font-normal text-sm">· Prioridad {getEventPriority(e)}</span>
            </h2>
            <p className="text-sm text-muted-foreground">{getCidDescription(e.cid_code)} (CID {e.cid_code})</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <DetailRow
              icon={User}
              label="Cliente / Cuenta"
              value={
                <>
                  {e.alarm_sites?.name ?? <span className="italic text-muted-foreground">Sin asignar</span>}
                  {e.alarm_sites?.account_number && (
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">#{e.alarm_sites.account_number}</span>
                  )}
                </>
              }
            />
            <DetailRow
              icon={MapPin}
              label="Zona"
              value={
                e.zone_no != null
                  ? `Z${e.zone_no}${e.zone_name ? ` — ${e.zone_name}` : ''}${e.partition_no > 1 ? ` · Partición ${e.partition_no}` : ''}`
                  : <span className="italic text-muted-foreground">Sin zona (evento general del panel)</span>
              }
            />
            <DetailRow icon={Radio} label="Categoría" value={CATEGORY_LABELS[e.event_category] ?? e.event_category} />
            <DetailRow
              icon={Hash}
              label="Código de usuario"
              value={e.user_code ? `${e.user_code}${e.user_name ? ` — ${e.user_name}` : ''}` : (
                e.armed_by_profile_id ? 'Vía app ASI Security' : <span className="italic text-muted-foreground">—</span>
              )}
            />
            <DetailRow icon={Clock} label="Ocurrió" value={fmtDatetime(e.occurred_at)} />
            <DetailRow icon={Clock} label="Recibido en ARC" value={fmtDatetime(e.received_at)} />
            <DetailRow
              icon={Shield}
              label="Reconocido por"
              value={e.ack_profile ? `${e.ack_profile.full_name} — ${fmtDatetime(e.acknowledged_at!)}` : '—'}
            />
            <DetailRow
              icon={Shield}
              label="Resuelto por"
              value={e.res_profile ? `${e.res_profile.full_name} — ${fmtDatetime(e.resolved_at!)}` : '—'}
            />
          </div>

          <div className="rounded-lg border p-3 bg-muted/30">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Resolución del operador ({operatorName})</div>
            <p className="text-sm">{e.resolution ?? <span className="italic text-muted-foreground">Sin nota de resolución</span>}</p>
          </div>

          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Tiempo de respuesta: </span>
              <span className="font-mono font-medium">{fmtResponseTime(e.response_time_seconds)}</span>
            </div>
          </div>

          {e.raw_message && (
            <details className="rounded-lg border p-3">
              <summary className="text-xs font-semibold text-muted-foreground cursor-pointer select-none">
                Mensaje crudo del panel (raw_message)
              </summary>
              <pre className="mt-2 text-[11px] bg-muted/50 rounded p-2 overflow-x-auto">
                {JSON.stringify(e.raw_message, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditPage;
